<template>
  <component
    :is="componentTag"
    v-bind="componentAttributes"
    :class="clsx('smart-link', {
      'is-disabled': disabled,
      'is-inactive': isInactive
    })"
    @click="handleClick"
    @touchend="handleClick"
    v-on="$listeners"
  >
    <slot />
  </component>
</template>

<script>
import clsx from 'clsx';
import HashAnchor from '../hash-anchor';
import RouterLinkWrapper from './router-link-wrapper';

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    activeClass: {
      type: String,
      default: 'router-link-active',
    },
    exactActiveClass: {
      type: String,
      default: 'router-link-exact-active',
    },
    url: {
      type: String,
      default: null,
    },
    route: {
      type: [Object, String],
      default: null,
    },
    target: {
      type: String,
      default: '_self',
      validator(value) { return ['_blank', '_self', '_parent', '_top'].includes(value); },
    },
    method: {
      type: Function,
      default: null,
    },
    event: {
      type: String,
      default: null,
    },
    eventData: {
      type: Object,
      default: null,
    },
    hash: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      touchHandled: false,
    };
  },
  components: { RouterLinkWrapper, HashAnchor },
  computed: {
    isSamePageHashLink() {
      if (!this.hash) return false;

      if (this.route) {
        const routeObject = typeof this.route === 'string'
          ? this.$router.resolve(this.route).route
          : this.$router.resolve(this.route).route;
        return routeObject.path === this.$route.path;
      }

      if (this.url) {
        const currentPath = window.location.pathname;
        const urlPath = this.url.split('#')[0];
        return urlPath === currentPath || urlPath === '';
      }

      return false;
    },
    componentTag() {
      if (this.isSamePageHashLink) return 'HashAnchor';
      if (this.route) return 'RouterLinkWrapper';
      if (this.url) return 'a';
      return 'span';
    },
    componentAttributes() {
      if (this.componentTag === 'HashAnchor') {
        return { hash: this.hash };
      }

      if (this.componentTag === 'RouterLinkWrapper') {
        return {
          to: this.route,
          activeClass: this.activeClass,
          exactActiveClass: this.exactActiveClass,
          hash: this.hash,
        };
      }

      if (this.componentTag === 'a') {
        const href = this.disabled ? '#' : this.url;
        const fullHref = this.hash && href && href !== '#' ? `${href}${this.hash}` : href;

        return {
          href: fullHref,
          target: this.disabled ? null : this.target,
        };
      }

      return {};
    },
    isInactive() {
      return (!this.url || this.url === '' || this.url === '#') && !this.route && !this.method && !this.event;
    },
  },
  methods: {
    clsx,
    handleClick(event) {
      if (this.componentTag === 'HashAnchor') {
        return;
      }

      if (event.type === 'click' && this.touchHandled) {
        this.touchHandled = false;
        if (this.componentTag !== 'a') {
          event.preventDefault();
        }
        return;
      }

      if (this.disabled || this.isInactive) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.type === 'touchend') {
        this.touchHandled = true;
      }

      event.stopPropagation();

      if (this.method) {
        this.method(event);
      }
      this.$emit('click', event);
      if (this.event) {
        this.$eventBus.$emit(this.event, this.eventData);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.smart-link {
  display: inline-block;
  cursor: pointer;
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.is-inactive {
  cursor: default;
}
</style>
